import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDoubleRight, faExclamationTriangle, faQuestionCircle } from '@fortawesome/pro-solid-svg-icons';
import pluralize from 'pluralize';

import { Alert } from 'components/ui/alert';
import { Popover } from '@buildoutinc/blueprint-react/ui/Popover';

export function MoveRecordsConfirmation({ sourceDatabase, targetDatabase, selectedPropertyIds }) {
  const count = selectedPropertyIds.length;
  const [portalContainer, setPortalContainer] = useState(null);
  // Portal popovers into the modal card so they stack above it and aren't
  // clipped by the scrollable modal body. Falls back to <body> outside a modal.
  const setRootRef = useCallback((node) => {
    setPortalContainer(node ? node.closest('.modal-content') : null);
  }, []);

  const infoMessage = () => {
    return (
      <>
        <h6 className="mb-4">Move Properties</h6>
        <div className="d-flex mb-2">
          <div className="align-self-center">
            <FontAwesomeIcon className="double-chevron text-primary me-3" icon={faChevronDoubleRight} size="sm" />
          </div>
          <div className="align-self-center">
            <strong>Property (Moved to Selected Database; Deleted from Current Database)</strong>
            <br />
            Property Information, Location Information, Building, Land, Multifamily Information and Media
          </div>
        </div>
        <div className="d-flex mb-2">
          <div className="align-self-center">
            <FontAwesomeIcon className="text-primary me-3" icon={faChevronDoubleRight} size="sm" />
          </div>
          <div className="align-self-center">
            <strong>Related Data (Copied to Selected Database; Remains from Current Database)</strong>
            <br />
            Activities and Tasks, Groups, Tags, Related Contacts, Sale and Lease Comps and Listings
          </div>
        </div>
      </>
    );
  };

  const firstDescription = () => {
    return (
      <div>
        Once moved to {targetDatabase.name}, these properties will be&nbsp;
        <strong>deleted</strong> from {sourceDatabase.name}.
      </div>
    );
  };

  const secondDescription = () => {
    return (
      <div>
        <strong>All related data will be copied</strong> to my {targetDatabase.name} and therefore&nbsp;
        <strong>exist in both</strong> {sourceDatabase.name} and {targetDatabase.name}
      </div>
    );
  };


  const moreInfo = () => {
    return (
      <Popover>
        <Popover.Trigger openOnHover render={<FontAwesomeIcon icon={faQuestionCircle} />} />
        <Popover.Content container={portalContainer} id="info-description" side="top" style={{ width: 550 }}>
          <Popover.Body>{infoMessage()}</Popover.Body>
        </Popover.Content>
      </Popover>
    );
  };

  return (
    <div className="d-flex flex-column gap-3" ref={setRootRef}>
      <p>
        {
          `${count} ${pluralize('property', count)} will be moved `
          + `from ${sourceDatabase.name} to ${targetDatabase.name}.`
        }
      </p>
      <Alert icon={faExclamationTriangle} severity="destructive">
        <div className="d-flex align-items-center gap-2">
          {firstDescription()}
          {moreInfo()}
        </div>
      </Alert>

      <Alert>
        <div className="d-flex align-items-center gap-2">
          {secondDescription()}
          {moreInfo()}
        </div>
      </Alert>
      <p>You will receive an email when the move is complete.</p>
    </div>
  );
}

MoveRecordsConfirmation.propTypes = {
  selectedPropertyIds: PropTypes.array.isRequired,
  sourceDatabase: PropTypes.object.isRequired,
  targetDatabase: PropTypes.object.isRequired
};


const mapStateToProps = state => ({
  selectedPropertyIds: state.selectList.selectedPropertyIds,
});

export default connect(mapStateToProps, null)(MoveRecordsConfirmation);
